import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import "./AddButton.scss";

export default function AddButton({
  onClick = () => {},
  tooltip = "Add New",
  size = 50,
  className = "",
  ariaLabel = "Add new item",
  disabled = false,
  text = "", // label shown next to the button (optional)
  textPosition = "right", // "left" | "right"
  textColor, // optional: overrides color; if not provided, uses default green
}) {
  const sizeAttr = typeof size === "number" ? `${size}px` : size;
  const tooltipRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const tooltip = tooltipRef.current;
    const btn = btnRef.current;
    if (!btn) return;

    const show = () => {
      if (tooltip) {
        gsap.killTweensOf(tooltip);
        gsap.to(tooltip, {
          opacity: 1,
          y: 0,
          duration: 0.25,
          ease: "power2.out",
        });
      }
      gsap.killTweensOf(btn);
      gsap.to(btn, { scale: 1.1, duration: 0.2, ease: "power2.out" });
    };

    const hide = () => {
      if (disabled) return;
      if (tooltip) {
        gsap.killTweensOf(tooltip);
        gsap.to(tooltip, {
          opacity: 0,
          y: 10,
          duration: 0.2,
          ease: "power2.in",
        });
      }
      gsap.killTweensOf(btn);
      gsap.to(btn, { scale: 1, duration: 0.2, ease: "power2.in" });
    };

    btn.addEventListener("mouseenter", show);
    btn.addEventListener("mouseleave", hide);

    // also hide on focus out for keyboard a11y
    btn.addEventListener("focus", show);
    btn.addEventListener("blur", hide);

    return () => {
      btn.removeEventListener("mouseenter", show);
      btn.removeEventListener("mouseleave", hide);
      btn.removeEventListener("focus", show);
      btn.removeEventListener("blur", hide);
    };
  }, [disabled]); // include disabled so hide respects updated prop

  // layout: label left or right
  const renderLabel = () =>
    text ? (
      <span
        className="add-btn__label"
        aria-hidden={disabled ? "true" : "false"}
      >
        {text}
      </span>
    ) : null;

  return (
    <div className={`add-btn-wrapper ${className}`}>
      <div
        className={`add-btn-container ${textPosition === "left" ? "label-left" : "label-right"}`}
      >
        {textPosition === "left" && renderLabel()}

        <button
          ref={btnRef}
          type="button"
          aria-label={ariaLabel}
          onClick={!disabled ? onClick : undefined}
          className={`add-btn ${disabled ? "disabled" : ""}`}
          disabled={disabled}
          // prevent accidental tab order change when disabled (native disabled remains)
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={sizeAttr}
            height={sizeAttr}
            viewBox="0 0 24 24"
            className="add-btn__icon"
            role="img"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="12" y1="8" x2="12" y2="16" />
          </svg>
        </button>

        {textPosition === "right" && renderLabel()}
      </div>

      {tooltip && (
        <span ref={tooltipRef} className="add-btn__tooltip">
          {tooltip}
        </span>
      )}
    </div>
  );
}
